import { Alert, Center, Loader, Paper, Stack } from '@mantine/core';
import { getFormattedDate } from '@mantine/dates';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { frequencyAmountCell, SectionTotal } from 'src/cashflows/components/cashflowCells';
import CashflowSection from 'src/cashflows/components/CashflowSection';
import CashflowTable, { CashflowColumn } from 'src/cashflows/components/CashflowTable';
import { labelForIncomeType } from 'src/cashflows/incomeTypes';
import { formatCurrency, Frequency } from 'src/cashflows/utils';
import { FamilyMember } from './useFamilyMembers';
import {
  FamilyExpenditure,
  FamilyIncome,
  FamilyLifeEvent,
  useFamilyCashflows,
} from './useFamilyCashflows';

export function FamilyCashflowPanel({ members }: { members: FamilyMember[] }) {
  const { incomes, expenditures, lifeEvents, isLoading, hasErrors } = useFamilyCashflows(members);

  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly');
  const [expenditureFrequency, setExpenditureFrequency] = useState<Frequency>('monthly');

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  const incomeMonthlyPreTax = incomes.reduce((sum, item) => sum + item.amountPreTaxMonthly, 0);
  const incomeMonthlyPostTax = incomes.reduce((sum, item) => sum + item.amountPostTaxMonthly, 0);
  const incomeAnnualPreTax = incomes.reduce((sum, item) => sum + item.amountPreTaxAnnually, 0);
  const incomeAnnualPostTax = incomes.reduce((sum, item) => sum + item.amountPostTaxAnnually, 0);

  const incomeIsMonthly = incomeFrequency === 'monthly';
  const incomeTotalPreTax = incomeIsMonthly ? incomeMonthlyPreTax : incomeAnnualPreTax;
  const incomeTotalPostTax = incomeIsMonthly ? incomeMonthlyPostTax : incomeAnnualPostTax;
  const incomeTotalTooltip = incomeIsMonthly
    ? `Annually: +${formatCurrency(incomeAnnualPreTax)} (${formatCurrency(incomeAnnualPostTax)} after tax)`
    : `Monthly: +${formatCurrency(incomeMonthlyPreTax)} (${formatCurrency(incomeMonthlyPostTax)} after tax)`;

  const monthlyExpenditure = expenditures.reduce((sum, item) => sum + item.amountMonthly, 0);
  const annualExpenditure = expenditures.reduce((sum, item) => sum + item.amountAnnually, 0);
  const expenditureTotal =
    expenditureFrequency === 'monthly' ? monthlyExpenditure : annualExpenditure;
  const expenditureTotalTooltip =
    expenditureFrequency === 'monthly'
      ? `Annually: -${formatCurrency(annualExpenditure)}`
      : `Monthly: -${formatCurrency(monthlyExpenditure)}`;

  return (
    <Stack gap="md">
      {hasErrors && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title="Some data could not be loaded"
        >
          Cashflow data for one or more family members failed to load, so the totals below may be
          incomplete.
        </Alert>
      )}

      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        frequency={incomeFrequency}
        onFrequencyChange={setIncomeFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={incomeTotalTooltip}>
            +{formatCurrency(incomeTotalPreTax)} ({formatCurrency(incomeTotalPostTax)} after tax)
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyIncome> items={incomes} columns={incomeColumns(incomeFrequency)} />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        frequency={expenditureFrequency}
        onFrequencyChange={setExpenditureFrequency}
        showFrequencyToggle
        totalNode={
          <SectionTotal tooltipLabel={expenditureTotalTooltip}>
            -{formatCurrency(expenditureTotal)}
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyExpenditure>
          items={expenditures}
          columns={expenditureColumns(expenditureFrequency)}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        totalNode={
          <SectionTotal>
            -{formatCurrency(lifeEvents.reduce((sum, item) => sum + (item.cost || 0), 0))}
          </SectionTotal>
        }
      >
        <CashflowTable<FamilyLifeEvent> items={lifeEvents} columns={lifeEventColumns} />
      </CashflowSection>
    </Stack>
  );
}

function incomeColumns(frequency: Frequency): CashflowColumn<FamilyIncome>[] {
  const preTaxKey = frequency === 'monthly' ? 'amountPreTaxMonthly' : 'amountPreTaxAnnually';
  const postTaxKey = frequency === 'monthly' ? 'amountPostTaxMonthly' : 'amountPostTaxAnnually';

  return [
    { key: 'memberName', header: 'Member' },
    {
      key: 'incomeType',
      header: 'Type',
      renderValue: value => labelForIncomeType(String(value)),
    },
    { key: 'description', header: 'Source' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: preTaxKey,
      header: 'Before tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountPreTaxMonthly, item.amountPreTaxAnnually, {
          positive: true,
        }),
    },
    {
      key: postTaxKey,
      header: 'After tax',
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountPostTaxMonthly, item.amountPostTaxAnnually, {
          positive: true,
        }),
    },
  ];
}

function expenditureColumns(frequency: Frequency): CashflowColumn<FamilyExpenditure>[] {
  const amountKey = frequency === 'monthly' ? 'amountMonthly' : 'amountAnnually';

  return [
    { key: 'memberName', header: 'Member' },
    { key: 'category', header: 'Category' },
    { key: 'frequency', header: 'Frequency' },
    {
      key: amountKey,
      header: `Spend (${frequency})`,
      renderValue: (_value, item) =>
        frequencyAmountCell(frequency, item.amountMonthly, item.amountAnnually),
    },
  ];
}

const lifeEventColumns: CashflowColumn<FamilyLifeEvent>[] = [
  { key: 'memberName', header: 'Member' },
  { key: 'description', header: 'Description' },
  {
    key: 'eventDate',
    header: 'Date',
    renderValue: value =>
      getFormattedDate({
        type: 'default',
        date: new Date(String(value)),
        locale: 'en',
        format: 'D MMMM YYYY',
        labelSeparator: '',
      }),
  },
  { key: 'details', header: 'Details' },
  {
    key: 'cost',
    header: 'Amount',
    renderValue: value => formatCurrency(Number(value)),
  },
];
